import { useState, useEffect } from 'react';
import {
  Play, RotateCcw, Loader2, Zap, Search, Database,
  ChevronDown, ChevronRight, X
} from 'lucide-react';
import { dataEngineApi } from '../../services/dataEngineApi';
import StatusBadge from './StatusBadge';

const ENTITY_TYPES = ['Accounts', 'Customers', 'Vendors', 'Invoices', 'Bills', 'CreditMemos', 'VendorCredits', 'Payments', 'Purchases', 'Items', 'Employees', 'Terms', 'Classes', 'Departments', 'Budgets', 'Estimates', 'BillPayments'];

const formatRelative = (d) => {
  if (!d) return null;
  try {
    const diff = (Date.now() - new Date(d)) / 3600000;
    if (diff < 1) return `${Math.round(diff * 60)}m ago`;
    if (diff < 24) return `${Math.round(diff)}h ago`;
    if (diff < 168) return `${Math.round(diff / 24)}d ago`;
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return null; }
};

/* ─── Inline Entity Data Viewer ──────────────────────── */
function EntityDataInline({ entityType, rfId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    dataEngineApi.getEntityData(entityType.toLowerCase(), rfId, page, PAGE_SIZE, search)
      .then(resp => setData(resp.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [entityType, rfId, page, search]);

  const rows = data?.content || [];
  const columns = data?.columns || [];
  const totalPages = data?.totalPages || 0;
  const totalElements = data?.totalElements || 0;

  const hideCols = new Set(['created_at', 'updated_at', 'qb_created_at', 'qb_updated_at']);
  const visibleCols = columns.filter(c => !hideCols.has(c));

  const formatCell = (val) => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'number') return val.toLocaleString();
    return String(val);
  };

  return (
    <div className="bg-gray-800/10 border-t border-gray-800/30 px-4 py-3">
      {/* Search */}
      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500" />
          <input
            type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search..."
            className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg pl-7 pr-3 py-1.5 text-[11px] text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600"
          />
        </div>
        <span className="text-[10px] text-gray-500">{totalElements.toLocaleString()} records</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-4 w-4 animate-spin text-gray-600" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-600">
          <Database className="h-6 w-6 mb-2 opacity-40" />
          <p className="text-[11px]">{search ? 'No matches' : 'No data. Sync this entity first.'}</p>
        </div>
      ) : (
        <div className="overflow-auto max-h-[400px] rounded-lg border border-gray-800">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-900 z-10">
              <tr className="border-b border-gray-800">
                {visibleCols.map(col => (
                  <th key={col} className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider text-left whitespace-nowrap">
                    {col.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={`border-b border-gray-800/30 hover:bg-gray-800/20 ${i % 2 ? 'bg-gray-900/20' : ''}`}>
                  {visibleCols.map(col => (
                    <td key={col} className="px-3 py-1.5 text-gray-300 whitespace-nowrap text-[11px] max-w-[180px] truncate" title={String(row[col] ?? '')}>
                      {formatCell(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-gray-500">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-2 py-0.5 text-[10px] text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded disabled:opacity-30 transition-all">Prev</button>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="px-2 py-0.5 text-[10px] text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded disabled:opacity-30 transition-all">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Entity Sync Tab ─────────────────────────────────── */
export default function EntitySyncTab({ company }) {
  const [statuses, setStatuses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingEntity, setSyncingEntity] = useState(null); // entityType being synced
  const [syncingAll, setSyncingAll] = useState(null); // 'smart' | null
  const [expandedEntity, setExpandedEntity] = useState(null);

  const rfId = company.rf_id;

  const fetchStatuses = () => {
    setLoading(true);
    dataEngineApi.getEntitySyncStatus(rfId)
      .then(resp => {
        const byType = {};
        for (const s of (resp.data || [])) byType[s.entityType] = s;
        setStatuses(byType);
      })
      .catch(() => setStatuses({}))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatuses(); }, [rfId]);

  const handleEntitySync = async (entityType, fullSync) => {
    setSyncingEntity(entityType);
    try {
      if (fullSync) {
        await dataEngineApi.triggerEntityFullSync(rfId, entityType);
      } else {
        await dataEngineApi.triggerEntityCdc(rfId, entityType);
      }
      // Refresh
      const resp = await dataEngineApi.getEntitySyncStatus(rfId);
      const byType = {};
      for (const s of (resp.data || [])) byType[s.entityType] = s;
      setStatuses(byType);
    } catch (err) {
      console.error(`Sync failed for ${entityType}:`, err);
    }
    setSyncingEntity(null);
  };

  const handleSyncAll = async () => {
    setSyncingAll('smart');
    for (const et of ENTITY_TYPES) {
      try {
        const s = statuses?.[et];
        if (s && s.status === 'COMPLETED') {
          await dataEngineApi.triggerEntityCdc(rfId, et);
        } else {
          await dataEngineApi.triggerEntityFullSync(rfId, et);
        }
      } catch {}
    }
    // Refresh
    const resp = await dataEngineApi.getEntitySyncStatus(rfId);
    const byType = {};
    for (const s of (resp.data || [])) byType[s.entityType] = s;
    setStatuses(byType);
    setSyncingAll(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Sync All button */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">Entity Types</h3>
        <button
          onClick={handleSyncAll}
          disabled={syncingAll !== null}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-lg hover:bg-blue-500/20 disabled:opacity-40 transition-all"
        >
          {syncingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
          Sync All
        </button>
      </div>

      {/* Entity rows */}
      <div className="bg-gray-800/30 border border-gray-800 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center border-b border-gray-800">
          <div className="px-4 py-2 text-[10px] text-gray-600 uppercase tracking-wider w-8 flex-shrink-0"></div>
          <div className="px-3 py-2 text-[10px] text-gray-600 uppercase tracking-wider w-36 flex-shrink-0">Entity</div>
          <div className="px-3 py-2 text-[10px] text-gray-600 uppercase tracking-wider w-24 flex-shrink-0">Status</div>
          <div className="px-3 py-2 text-[10px] text-gray-600 uppercase tracking-wider text-right w-20 flex-shrink-0">Records</div>
          <div className="px-3 py-2 text-[10px] text-gray-600 uppercase tracking-wider w-24 flex-shrink-0">Last Sync</div>
          <div className="px-3 py-2 text-[10px] text-gray-600 uppercase tracking-wider flex-1">Error</div>
          <div className="px-3 py-2 text-[10px] text-gray-600 uppercase tracking-wider text-right w-32 flex-shrink-0">Actions</div>
        </div>

        {/* Entity type rows */}
        {ENTITY_TYPES.map(et => {
          const s = statuses?.[et] || {};
          const isSyncing = syncingEntity === et;
          const neverSynced = !s.status;
          const lastSync = formatRelative(s.lastSyncCompletedAt || s.lastCdcSyncAt || s.lastFullSyncAt);
          const isExpanded = expandedEntity === et;

          return (
            <div key={et}>
              <div
                className="flex items-center border-b border-gray-800/20 hover:bg-gray-800/10 transition-colors cursor-pointer"
                onClick={() => setExpandedEntity(isExpanded ? null : et)}
              >
                <div className="px-4 py-2 w-8 flex-shrink-0">
                  {isExpanded
                    ? <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                    : <ChevronRight className="h-3.5 w-3.5 text-gray-600" />}
                </div>
                <div className="px-3 py-2 text-sm text-gray-300 w-36 flex-shrink-0">{et}</div>
                <div className="px-3 py-2 w-24 flex-shrink-0">
                  {neverSynced
                    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-gray-800 text-gray-500 border border-gray-700">Not synced</span>
                    : <StatusBadge status={s.status} />}
                </div>
                <div className="px-3 py-2 text-sm font-mono tabular-nums text-right w-20 flex-shrink-0 text-gray-400">
                  {s.recordsCount > 0 ? s.recordsCount.toLocaleString() : '—'}
                </div>
                <div className="px-3 py-2 text-sm text-gray-500 w-24 flex-shrink-0">{lastSync || '—'}</div>
                <div className="px-3 py-2 flex-1 min-w-0">
                  {s.errorMessage && (
                    <span className="text-[10px] text-red-400 truncate block" title={s.errorMessage}>
                      {s.errorMessage.length > 50 ? s.errorMessage.slice(0, 50) + '...' : s.errorMessage}
                    </span>
                  )}
                </div>
                <div className="px-3 py-2 w-32 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      onClick={() => handleEntitySync(et, neverSynced)}
                      disabled={isSyncing}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-blue-400 hover:bg-blue-500/10 rounded transition-colors disabled:opacity-40"
                      title="Incremental sync (CDC)"
                    >
                      {isSyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Sync
                    </button>
                    <button
                      onClick={() => handleEntitySync(et, true)}
                      disabled={isSyncing}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-amber-400 hover:bg-amber-500/10 rounded transition-colors disabled:opacity-40"
                      title="Delete all + re-fetch"
                    >
                      {isSyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Full
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded inline data view */}
              {isExpanded && s.recordsCount > 0 && (
                <EntityDataInline entityType={et} rfId={rfId} />
              )}
              {isExpanded && (!s.recordsCount || s.recordsCount === 0) && (
                <div className="bg-gray-800/10 border-t border-gray-800/30 px-4 py-6 text-center text-gray-600 text-xs">
                  No data. Sync this entity first.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
